import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, CalendarOff } from "lucide-react";
import { Badge, Empty, Loading } from "./common";
import { Input } from "./ui/input";
import { useApi } from "../lib/api";
import { eur, fmtLong, num, todayISO } from "../lib/format";

export function OffDayBanner({ reason }) {
  if (!reason) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-sky-700 bg-sky-950/60 text-sky-100 px-4 py-3 mb-4 text-sm" data-testid="off-day-banner">
      <CalendarOff className="h-4 w-4 shrink-0" /><span><b>Giorno non lavorativo ({reason})</b>: ore e costi non vengono conteggiati.</span>
    </div>
  );
}

export function SlotList({ slots, showCost }) {
  return (
    <ul className="space-y-1.5">
      {slots.map((s) => (
        <li key={s.assignment_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm rounded-md bg-background/60 border border-border px-3 py-2" data-testid={`worker-slot-${s.assignment_id}`}>
          <span className="num font-semibold text-amber-300 flex items-center gap-1.5 w-[110px]"><Clock className="h-3.5 w-3.5" />{s.shift}</span>
          <Link to={`/cantieri/${s.site_id}`} className="flex-1 min-w-[140px] hover:text-amber-300"><span className="font-mono text-xs text-muted-foreground">{s.site_code}</span> {s.site_name}</Link>
          <span className="num text-muted-foreground">{num(s.hours)} h</span>
          {showCost && <span className="num w-24 text-right">{eur(s.cost)}</span>}
        </li>
      ))}
    </ul>
  );
}

function WorkerDayRow({ r, showCost }) {
  return (
    <div className="surface p-4" data-testid={`worker-day-${r.worker_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <Link to={`/operai/${r.worker_id}`} className="font-semibold hover:text-amber-300">{r.name}</Link>
          <span className="text-xs text-muted-foreground"> · {r.qualification}</span>
        </div>
        <div className="flex items-center gap-2">
          {r.site_count > 1 && <Badge className="bg-sky-950 border-sky-600 text-sky-200" testid={`worker-day-multi-${r.worker_id}`}><MapPin className="h-3 w-3 mr-1" />{r.site_count} cantieri</Badge>}
          <span className="num font-semibold" data-testid={`worker-day-hours-${r.worker_id}`}>{num(r.hours)} h</span>
          {showCost && <span className="num text-amber-300">{eur(r.cost)}</span>}
        </div>
      </div>
      <SlotList slots={r.slots} showCost={showCost} />
    </div>
  );
}

export function WorkerDayList({ rows, showCost }) {
  if (!rows.length) return <div className="surface"><Empty title="Nessun operaio al lavoro in questa data" /></div>;
  return <div className="grid xl:grid-cols-2 gap-3" data-testid="worker-day-list">{rows.map((r) => <WorkerDayRow key={r.worker_id} r={r} showCost={showCost} />)}</div>;
}

function DayContent({ data }) {
  if (!data) return <Loading />;
  const w = data.worker;
  if (!w) return <Empty title="Nessun cantiere in questa data" />;
  return (
    <>
      <div className="flex flex-wrap gap-3 mb-3 text-sm">
        <span data-testid="worker-day-panel-sites">{w.site_count} {w.site_count === 1 ? "cantiere" : "cantieri"}</span>
        <span className="num font-semibold" data-testid="worker-day-panel-hours">{num(w.hours)} ore</span>
        <span className="num text-amber-300">{eur(w.cost)}</span>
      </div>
      <SlotList slots={w.slots} showCost />
    </>
  );
}

export function WorkerDayPanel({ workerId }) {
  const [day, setDay] = useState(todayISO());
  const { data } = useApi(`/operations/workers/${workerId}`, { day });
  return (
    <div className="surface p-5" data-testid="worker-day-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="font-semibold capitalize">{fmtLong(day)}</div>
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value || todayISO())} className="w-auto" data-testid="worker-day-date-input" />
      </div>
      <OffDayBanner reason={data?.date === day ? data.off_reason : null} />
      <DayContent data={data?.date === day ? data : null} />
    </div>
  );
}
